import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@/components/ui/Tooltip'
import { CheckIcon, DetailsIcon, DragHandleIcon, TrashIcon } from '@/components/ui/icons'
import type { DatabaseDialect } from '@/types'
import { sanitizeSqlIdentifierInput } from '@/utils/sqlIdentifiers'
import { ColumnTypeInput } from './ColumnTypeInput'
import type { ColumnImpact, DraftColumn } from './tableEditorTypes'

interface ColumnRowProps {
  column: DraftColumn
  impact: ColumnImpact
  isDeleteConfirming: boolean
  isEntering?: boolean
  autoFocusName?: boolean
  onDeleteClick: (rowId: string) => void
  onToggleDescription: (rowId: string) => void
  onChangeField: <K extends keyof DraftColumn>(rowId: string, field: K, value: DraftColumn[K]) => void
  dialect?: DatabaseDialect
}

export function ColumnRow({
  column,
  impact,
  isDeleteConfirming,
  isEntering = false,
  autoFocusName = false,
  dialect,
  onDeleteClick,
  onToggleDescription,
  onChangeField,
}: ColumnRowProps) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.rowId })

  return (
    <div
      ref={setNodeRef}
      style={{
        opacity: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        animation: isEntering ? 'morphColumnRowIn 280ms ease-out both' : 'none',
      }}
      {...attributes}
    >
      <div
        style={{
          opacity: isDragging ? 0.72 : 1,
          borderColor: 'var(--border)',
          background: 'var(--bg-card)',
        }}
        className="overflow-hidden rounded-xl border px-2 py-1"
      >
      {/* Main row — drag | flags | name | type | NN | BL | desc | delete */}
      <div
        className="grid items-center gap-1.5"
        style={{
          gridTemplateColumns: '20px minmax(0,1fr) minmax(0,188px) 20px 20px 20px 24px 24px 28px 28px',
        }}
      >
        {/* Drag handle — only listeners go on the activator */}
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...listeners}
          className="flex h-7 w-5 items-center justify-center border-none bg-transparent p-0 text-[11px] transition-colors"
          style={{
            color: 'var(--text-dim)',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          title={t('prompts.reorderColumn')}
        >
          <DragHandleIcon />
        </button>

        {/* Name input */}
        <input
          type="text"
          value={column.name}
          autoFocus={autoFocusName}
          placeholder={t('prompts.columnNamePlaceholder')}
          onChange={(event) => onChangeField(column.rowId, 'name', sanitizeSqlIdentifierInput(event.target.value))}
          className="morph-input min-w-0 px-2"
        />

        {/* Type input */}
        <ColumnTypeInput
          value={column.type}
          onChange={(value) => onChangeField(column.rowId, 'type', value)}
          dialect={dialect}
        />

        <label className="flex items-center justify-center" title={t('tableModal.pkTitle')}>
          <input
            type="checkbox"
            checked={!!column.pk}
            onChange={(event) => onChangeField(column.rowId, 'pk', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('tableModal.fkTitle')}>
          <input
            type="checkbox"
            checked={!!column.fk}
            onChange={(event) => onChangeField(column.rowId, 'fk', event.target.checked)}
            className="morph-checkbox"
          />
        </label>
        <label className="flex items-center justify-center" title={t('tableModal.ukTitle')}>
          <input
            type="checkbox"
            checked={!!column.uq}
            onChange={(event) => onChangeField(column.rowId, 'uq', event.target.checked)}
            className="morph-checkbox"
          />
        </label>

        {/* NN checkbox */}
        <label className="flex items-center justify-center" title={t('prompts.notNullTitle')}>
          <input
            type="checkbox"
            checked={column.notNull}
            onChange={(event) => onChangeField(column.rowId, 'notNull', event.target.checked)}
            className="morph-checkbox"
          />
        </label>

        {/* BL checkbox */}
        <label className="flex items-center justify-center" title={t('prompts.blankTitle')}>
          <input
            type="checkbox"
            checked={column.blank}
            onChange={(event) => onChangeField(column.rowId, 'blank', event.target.checked)}
            className="morph-checkbox"
          />
        </label>

        {/* Description toggle */}
        <button
          type="button"
          onClick={() => onToggleDescription(column.rowId)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] transition-colors"
          style={{
            color: column.desc || column.defaultValue || column.checkExpression || column.descOpen ? '#8B5CF6' : 'var(--text-dim)',
            background: column.descOpen ? 'rgba(139,92,246,.1)' : 'rgba(99,102,241,.04)',
          }}
          title={t('prompts.columnDescriptionTitle')}
        >
          <DetailsIcon />
        </button>

        {/* Delete button */}
        <Tooltip content={isDeleteConfirming ? t('prompts.removeColumnConfirm') : t('prompts.deleteFkColumn')} open={isDeleteConfirming}>
          <button
            type="button"
            onClick={() => onDeleteClick(column.rowId)}
            className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] transition-colors"
            style={{
              color: isDeleteConfirming ? 'var(--color-warning)' : 'var(--color-danger)',
              background: isDeleteConfirming ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)',
            }}
            title={isDeleteConfirming ? t('prompts.removeColumnConfirm') : t('prompts.deleteFkColumn')}
          >
            {isDeleteConfirming ? <CheckIcon /> : <TrashIcon />}
          </button>
        </Tooltip>
      </div>

      {/* Impact warning (only shown during delete confirmation) */}
      {isDeleteConfirming && (impact.hasForeignKeyRelation || impact.hasIndexImpact) ? (
        <div className="mt-1.5 rounded-lg border px-2 py-1.5 text-[10px]" style={{ borderColor: 'var(--color-warning-border)', background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>
          {t('prompts.columnImpactMessage', {
            targets: [
              impact.hasForeignKeyRelation ? t('prompts.columnImpactRelations') : null,
              impact.hasIndexImpact ? t('prompts.columnImpactIndexes') : null,
            ].filter(Boolean).join(` ${t('common.and')} `),
          })}
        </div>
      ) : null}

      {/* Description textarea (expanded) */}
      {column.descOpen ? (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="min-w-0 text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-dim)' }}>
              <span className="mb-1 block">DEFAULT</span>
              <input
                type="text"
                value={column.defaultValue ?? ''}
                placeholder={t('prompts.columnDefaultPlaceholder')}
                onChange={(event) => onChangeField(column.rowId, 'defaultValue', event.target.value)}
                className="morph-input min-w-0 normal-case tracking-normal"
              />
            </label>
            <label className="min-w-0 text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-dim)' }}>
              <span className="mb-1 block">CHECK</span>
              <input
                type="text"
                value={column.checkExpression ?? ''}
                placeholder={t('prompts.columnCheckPlaceholder')}
                onChange={(event) => onChangeField(column.rowId, 'checkExpression', event.target.value)}
                className="morph-input min-w-0 normal-case tracking-normal"
              />
            </label>
          </div>
          <textarea
            value={column.desc}
            placeholder={t('prompts.columnDescriptionPlaceholder')}
            onChange={(event) => onChangeField(column.rowId, 'desc', event.target.value)}
            className="morph-textarea min-h-[60px] resize-y"
          />
        </div>
      ) : null}
      </div>
    </div>
  )
}
